import { useFormik } from "formik"
import { useEffect, useMemo } from "react"
import * as Yup from "yup"
import { useParams, useLocation } from "react-router-dom"

import { useUI } from "providers/UIProvider"

import { TType } from "types/shared"
import { useSettings } from "providers/SettingsProvider/SettingsProvider"
import { useAppContext } from "providers/AppProvider/AppProvider"
import { generateFeeHistoryFromScheduleInterval } from "utils/feeHistoryUtils"
import { UI_INITIAL_STATE } from "providers/UIProvider/UIProviderConstants"


const BOOKING_SETTINGS_SCHEMA = (T: TType) => Yup.object({
  interval: Yup.lazy(value=>{
    if(value === "") {
      return Yup.string()
    }

    return  Yup
    .number()
    .required(T("booking_settings.form.validations.interval_required"))
    .max(60, T("booking_settings.form.validations.interval_invalid"))
  })
})

const useBookingSettings = () => {
  const params = useParams()
  const { pathname } = useLocation()
  const { laundry, branches, selectedBranches } = useAppContext()
  const { scheduleInterval, handleChangeScheduleIntervalSubmit } = useSettings()
  const { T, toggleModal } = useUI()
  const laundryId = Number(params?.laundryId) || laundry?.id || null

  const onSubmit = (val: any) => {
    if(pathname === "/settings/bookings") {
      toggleModal("feeHistoryModal", true, {
        data: generateFeeHistoryFromScheduleInterval({ T, inputData: branches.data, selectedBranches, scheduleInterval: Number(val.interval)}),
        handleDeny: () => toggleModal("feeHistoryModal", false, UI_INITIAL_STATE.modals.feeHistoryModal.data),
        handleSubmit: () => {
          handleChangeScheduleIntervalSubmit(selectedBranches, Number(val.interval))
          resetForm()
        }
      })
    } else if(laundryId !== null) {
      handleChangeScheduleIntervalSubmit([laundryId], val.interval)
    } 
  }

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, resetForm } = useFormik({
    initialValues: { 
      interval: "",
      placeholderInterval: "",
  },
    validationSchema: BOOKING_SETTINGS_SCHEMA(T),
    onSubmit
  })
  
  useEffect(()=>{
    if(scheduleInterval !== undefined && pathname !== "/settings/bookings") {
      setFieldValue("placeholderInterval", scheduleInterval)
    }
  },[scheduleInterval])
  
  useEffect(() => {
    if(scheduleInterval !== undefined && pathname === "/settings/bookings" && selectedBranches.length === 1) {
      setFieldValue("placeholderInterval", scheduleInterval)
      setFieldValue("interval", "")
    } else if(scheduleInterval !== undefined && pathname === "/settings/bookings" && selectedBranches.length === 0) {
      setFieldValue("placeholderInterval", "Choose Laundry")
      setFieldValue("interval", "")
    } else if(scheduleInterval !== undefined && pathname === "/settings/bookings" && selectedBranches.length > 1) {
      setFieldValue("placeholderInterval", "Choose One Laundry")
      setFieldValue("interval", "")
    }
  },[scheduleInterval, selectedBranches])


  return useMemo(
    () => ({
      form: {
        values,
        errors,
        touched,
        handleSubmit,
        handleChange,
        handleBlur,
        setFieldValue
      },
    }),
    [values, errors, touched],
  )
}

export default useBookingSettings